/**
 * @fileoverview Step 2 (company information) UI for the Register page.
 * @module pages/register/RegisterStep2
 */
import Select from "react-select";
import { validateRFC } from "../../utils/common.js";
import {
  filterSelectOptionByNombre,
  getSelectOptionNombre,
} from "./registerFormHelpers.js";
import styles from "../../styles/Register.module.css";

const RegisterStep2 = ({
  t,
  values,
  sectors,
  subSectors,
  branches,
  selectSectorRef,
  selectSubsectorRef,
  selectBranchRef,
  notSingleSelectStyles,
  subsectorSelectStyles,
  singleSelectStyles,
  getSafeSelectedOption,
  noOptionsMessage,
  formatOptionNombreLabel,
  handleRfcChange,
  handleForeignerToggle,
  handleNamedInputChange,
  handleSectorChange,
  handleSubsectorChange,
  handleBranchChange,
}) => {
  const rfcFormatInvalid =
    !values.esExtranjero &&
    values.RFC.trim() !== "" &&
    !validateRFC(values.RFC).isValid;
  const taxIdFormatInvalid =
    values.esExtranjero &&
    values.taxId.trim() !== "" &&
    /\s/.test(values.taxId.trim());

  return (
  <div className={styles.formStep2}>
    <div className={styles.formStepTitle}>
      2. {t("pages.register.step2Title1")}{" "}
      <span className={styles.formStepTitle2}>
        {t("pages.register.step2Title2")}
      </span>
    </div>
    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div
          className={`${styles.formLabel} ${
            values.esExtranjero ? styles.disabledLabel : ""
          }`}
        >
          {!values.esExtranjero ? "*" : ""}
          {t("pages.register.companyRFCLabel")}
        </div>
        <div className={styles.formInput}>
          <input
            type="text"
            value={values.RFC}
            onChange={handleRfcChange}
            className={`${styles.formSingleInput} ${
              rfcFormatInvalid ? styles.inputError : ""
            }`}
            placeholder={t("pages.register.companyRFCPlaceholder")}
            disabled={values.esExtranjero}
          />
          {rfcFormatInvalid && (
            <p className={styles.errorMessage}>
              {t("errors.validation.INVALID_RFC")}
            </p>
          )}
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.checkboxContainer}>
          <input
            type="checkbox"
            id="foreigner"
            checked={values.esExtranjero}
            onChange={handleForeignerToggle}
            className={styles.checkbox}
          />
          <label htmlFor="foreigner" className={styles.checkboxLabel}>
            {t("pages.register.foreignerLabel")}
          </label>
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div
          className={`${styles.formLabel} ${
            !values.esExtranjero ? styles.disabledLabel : ""
          }`}
        >
          {values.esExtranjero ? "*" : ""}
          {t("pages.register.taxIdLabel")}
        </div>
        <div className={styles.formInput}>
          <input
            type="text"
            name="taxId"
            value={values.taxId}
            onChange={handleNamedInputChange}
            className={`${styles.formSingleInput} ${
              taxIdFormatInvalid ? styles.inputError : ""
            }`}
            placeholder={t("pages.register.taxIdPlaceholder")}
            disabled={!values.esExtranjero}
          />
          {taxIdFormatInvalid && (
            <p className={styles.errorMessage}>
              {t("errors.validation.INVALID_TAX_ID")}
            </p>
          )}
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          *{t("pages.register.socialReasonLabel")}
        </div>
        <div className={styles.formInput}>
          <input
            type="text"
            name="razonSocial"
            value={values.razonSocial}
            onChange={handleNamedInputChange}
            className={styles.formNotSingleInput}
            placeholder={t("pages.register.socialReasonPlaceholder")}
          />
        </div>
      </div>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          *{t("pages.register.commercialNameLabel")}
        </div>
        <div className={styles.formInput}>
          <input
            type="text"
            name="nombreComercial"
            value={values.nombreComercial}
            onChange={handleNamedInputChange}
            className={styles.formNotSingleInput}
            placeholder={t("pages.register.commercialNamePlaceholder")}
          />
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          *{t("pages.register.sectorLabel")}
        </div>
        <Select
          ref={selectSectorRef}
          options={sectors}
          value={getSafeSelectedOption(sectors, values.sectorEconomico)}
          isSearchable={true}
          filterOption={filterSelectOptionByNombre}
          noOptionsMessage={noOptionsMessage}
          onChange={handleSectorChange}
          placeholder={t("pages.register.sectorPlaceholder")}
          styles={notSingleSelectStyles}
          getOptionValue={getSelectOptionNombre}
          formatOptionLabel={formatOptionNombreLabel}
        />
      </div>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.subsectorLabel")}
          <Select
            ref={selectSubsectorRef}
            options={subSectors}
            value={getSafeSelectedOption(subSectors, values.subSectorSCIAN)}
            isSearchable={true}
            filterOption={filterSelectOptionByNombre}
            noOptionsMessage={noOptionsMessage}
            onChange={handleSubsectorChange}
            placeholder={t("pages.register.subsectorPlaceholder")}
            styles={subsectorSelectStyles}
            getOptionValue={getSelectOptionNombre}
            formatOptionLabel={formatOptionNombreLabel}
          />
        </div>
      </div>
    </div>

    <div className={styles.formRow}>
      <div className={styles.formColumn}>
        <div className={styles.formLabel}>
          {t("pages.register.branchLabel")}
        </div>
        <Select
          ref={selectBranchRef}
          options={branches}
          value={getSafeSelectedOption(branches, values.ramaSCIAN)}
          isSearchable={true}
          filterOption={filterSelectOptionByNombre}
          noOptionsMessage={noOptionsMessage}
          onChange={handleBranchChange}
          placeholder={t("pages.register.branchPlaceholder")}
          styles={singleSelectStyles}
          getOptionValue={getSelectOptionNombre}
          formatOptionLabel={formatOptionNombreLabel}
        />
      </div>
    </div>

    <p className={styles.formInstructions}>
      *{t("pages.register.markedFieldsAreRequired")}
    </p>
  </div>
  );
};

export default RegisterStep2;
